// src/verify.ts
var RESULT_RANGE = 1e6;
var encoder = new TextEncoder();
function toHex(buffer) {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, "0")).join("");
}
async function sha256Hex(input) {
  return toHex(await crypto.subtle.digest("SHA-256", encoder.encode(input)));
}
async function hmacSha512Hex(key, message) {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    encoder.encode(key),
    { name: "HMAC", hash: "SHA-512" },
    false,
    ["sign"]
  );
  return toHex(await crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(message)));
}
function convertResultToNumber(result) {
  const substr = result.substring(0, 5);
  let val = parseInt(substr, 16);
  let index = 5;
  while (val > RESULT_RANGE - 1) {
    if (index + 5 >= result.length) {
      val = parseInt(result.substring(index, result.length), 16);
      break;
    }
    val = parseInt(result.substring(index, index + 5), 16);
    index += 5;
  }
  return val % RESULT_RANGE;
}
async function computeRoll(args) {
  const resultString = await hmacSha512Hex(
    args.serverSeed,
    args.clientSeed + args.salt + String(args.nonce)
  );
  return { resultString, resultNumber: convertResultToNumber(resultString) };
}
function chooseFromResult(options, result) {
  let totalWeight = 0;
  for (const option of options) totalWeight += option.weight ?? 0;
  if (totalWeight !== RESULT_RANGE) throw new Error("weights not set up properly");
  let r = result;
  for (let i = 0; i < options.length; i++) {
    r -= options[i]?.weight ?? 0;
    if (r < 0) return i;
  }
  throw new Error("unable to choose from weights");
}
async function verifyDropTableHash(bundle) {
  return await sha256Hex(JSON.stringify(bundle.canonicalEntries)) === bundle.dropTableHash;
}
function canonicalEntryItemId(entry) {
  return entry.catalog_item_id ?? entry.vendor_listing_id ?? entry.external_item_id ?? "";
}
function canonicalEntryItemSource(entry) {
  if (entry.item_source !== "EXTERNAL") return entry.item_source;
  return entry.published_source ?? "CATALOG";
}
function itemMatchesEntry(item, entry) {
  return item.weight === entry.weight && item.itemSource === canonicalEntryItemSource(entry) && item.itemId === canonicalEntryItemId(entry) && item.value === entry.value_cents && (item.listingPriceCents ?? null) === entry.price_cents && (item.isLimited ?? false) === entry.is_limited && (item.isGemSubstitute ?? false) === entry.is_gem_substitute && item.name === entry.name && item.imageUrl === entry.image_url;
}
function dropTableMatchesEntries(bundle) {
  if (bundle.dropTable.length !== bundle.canonicalEntries.length) return false;
  return bundle.canonicalEntries.every((entry, index) => {
    const item = bundle.dropTable[index];
    return item !== void 0 && itemMatchesEntry(item, entry);
  });
}
async function verifyOpen(bundle) {
  const dropTableHashMatches = await verifyDropTableHash(bundle);
  const entriesMatch = dropTableMatchesEntries(bundle);
  if (!bundle.seed.seed) {
    return {
      valid: false,
      seedRevealed: false,
      serverSeedMatchesHash: null,
      dropTableHashMatches,
      dropTableMatchesEntries: entriesMatch,
      computedResult: null,
      computedItemIndex: null,
      computedItemId: null
    };
  }
  const serverSeedMatchesHash = await sha256Hex(bundle.seed.seed) === bundle.seed.seedHash;
  const { resultNumber } = await computeRoll({
    serverSeed: bundle.seed.seed,
    clientSeed: bundle.open.clientSeed,
    salt: bundle.salt,
    nonce: bundle.open.nonce
  });
  const computedItemIndex = chooseFromResult(bundle.dropTable, resultNumber);
  const computedItem = bundle.dropTable[computedItemIndex];
  const valid = serverSeedMatchesHash && dropTableHashMatches && entriesMatch && resultNumber === bundle.open.result && computedItemIndex === bundle.open.itemIndex && computedItem?.itemId === bundle.open.item.itemId;
  return {
    valid,
    seedRevealed: true,
    serverSeedMatchesHash,
    dropTableHashMatches,
    dropTableMatchesEntries: entriesMatch,
    computedResult: resultNumber,
    computedItemIndex,
    computedItemId: computedItem?.itemId ?? null
  };
}
export {
  RESULT_RANGE,
  chooseFromResult,
  computeRoll,
  dropTableMatchesEntries,
  sha256Hex,
  verifyDropTableHash,
  verifyOpen
};
//# sourceMappingURL=verify.js.map